/**
 * Library file I/O — single object and collection envelopes.
 *
 * Library files are smaller than scene files (no app state, no camera, no
 * blend refs) so encode/decode runs synchronously on the main thread via
 * `codec.ts`. The persistence worker is reserved for full scene documents.
 */

import type { SceneObject } from "../modeling/types.ts";
import { decodeSceneObject, encodeSceneObject } from "./persistence/codec.ts";
import {
  type LibraryCollectionDocument,
  type LibraryObjectDocument,
  POSEREF_MAGIC,
  POSEREF_VERSION,
  type SceneDocument,
  type SerializedSceneObject,
} from "./persistence/types.ts";

// ---------------------------------------------------------------------------
// Build (live → serialized)
// ---------------------------------------------------------------------------

export function buildObjectFile(obj: SceneObject): LibraryObjectDocument {
  const now = Date.now();
  return {
    magic: POSEREF_MAGIC,
    version: POSEREF_VERSION,
    kind: "object",
    createdAt: now,
    modifiedAt: now,
    object: encodeSceneObject(obj),
  };
}

/**
 * Build a collection envelope from N objects. The world-space centroid is
 * computed and stored; each object's position is rewritten relative to it
 * so the group can be re-spawned anywhere without losing internal layout.
 */
export function buildCollectionFile(
  objects: SceneObject[],
): LibraryCollectionDocument {
  const now = Date.now();
  const n = objects.length;
  let cx = 0,
    cy = 0,
    cz = 0;
  for (const o of objects) {
    cx += o.position[0];
    cy += o.position[1];
    cz += o.position[2];
  }
  const centroid: [number, number, number] = [cx / n, cy / n, cz / n];

  const serialized = objects.map((o) => {
    const enc = encodeSceneObject(o);
    enc.position = [
      o.position[0] - centroid[0],
      o.position[1] - centroid[1],
      o.position[2] - centroid[2],
    ];
    return enc;
  });

  return {
    magic: POSEREF_MAGIC,
    version: POSEREF_VERSION,
    kind: "collection",
    createdAt: now,
    modifiedAt: now,
    centroid,
    objects: serialized,
  };
}

// ---------------------------------------------------------------------------
// Parse (JSON → envelope)
// ---------------------------------------------------------------------------

export type ParsedLibraryFile =
  | LibraryObjectDocument
  | LibraryCollectionDocument
  | SceneDocument;

/**
 * Parse a `.pr` file into one of the three envelope types. Throws if the
 * magic, version, or kind don't match the current schema.
 *
 * No backward compatibility — any older `.pr` envelope version is rejected
 * at the version gate.
 */
export function parseLibraryFile(json: string): ParsedLibraryFile {
  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch {
    throw new Error("Library file is not valid JSON.");
  }
  if (
    !data ||
    typeof data !== "object" ||
    (data as { magic?: unknown }).magic !== POSEREF_MAGIC
  ) {
    throw new Error("Not a PoseRef file.");
  }
  const d = data as { version?: unknown; kind?: unknown };
  if (d.version !== POSEREF_VERSION) {
    throw new Error(
      `Unsupported PoseRef file version: ${String(d.version)} (expected ${POSEREF_VERSION}).`,
    );
  }
  if (d.kind === "object" || d.kind === "collection" || d.kind === "scene") {
    return data as ParsedLibraryFile;
  }
  throw new Error(`Unknown PoseRef file kind: ${String(d.kind)}`);
}

/**
 * Decode a `SerializedSceneObject` from a library envelope into a live
 * `SceneObject`. Convenience re-export — equivalent to
 * `decodeSceneObject` from `codec.ts`.
 */
export function decodeLibraryObject(s: SerializedSceneObject): SceneObject {
  return decodeSceneObject(s);
}
